import React, { useRef, useState } from 'react';
import './WhatsAppMessenger.css';
import WatconnectApi from '../../../api/WatconnectApi';
import DataApi from '../../../api/DataApi';

const FileAttachmentModal = ({
  isOpen,
  onClose,
  recipientPhone,
  selectedWhatsAppSetting,
  recipientName,
  onSendComplete,
}) => {
  const fileInputRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);

  const MAX_FILES = 10;

  const FILE_LIMITS = {
    image: 5 * 1024 * 1024, // 5MB
    video: 16 * 1024 * 1024, // 16MB
    audio: 16 * 1024 * 1024, // 16MB
    document: 100 * 1024 * 1024, // 100MB
  };

  const fileCategories = {
    document: {
      label: 'Document',
      accept: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt',
      icon: 'fa-regular fa-file-lines',
      type: 'document',
      description: 'PDF, Word, Excel, PPT, Text',
      color: '#e53e3e',
    },
    image: {
      label: 'Image',
      accept: '.jpeg,.jpg,.png',
      icon: 'fa-regular fa-image',
      type: 'image',
      description: 'JPEG, PNG',
      color: '#38a169',
    },
    audio: {
      label: 'Audio',
      accept: '.mp3,.aac,.m4a,.ogg,.amr',
      icon: 'fa-solid fa-music',
      type: 'audio',
      description: 'MP3, AAC, M4A, OGG',
      color: '#d69e2e',
    },
    video: {
      label: 'Video',
      accept: '.mp4,.3gp',
      icon: 'fa-solid fa-video',
      type: 'video',
      description: 'MP4, 3GP',
      color: '#3182ce',
    },
  };

  const getFileIcon = file => {
    const extension = file.name.split('.').pop().toLowerCase();

    if (file.type.startsWith('image/')) {
      return 'fa-regular fa-image wa-file-type-image';
    } else if (file.type.startsWith('video/')) {
      return 'fa-solid fa-video wa-file-type-video';
    } else if (file.type.startsWith('audio/')) {
      return 'fa-solid fa-music wa-file-type-audio';
    } else {
      // Document icons based on file type
      const docIcons = {
        pdf: 'fa-solid fa-file-pdf',
        doc: 'fa-solid fa-file-word',
        docx: 'fa-solid fa-file-word',
        xls: 'fa-solid fa-file-excel',
        xlsx: 'fa-solid fa-file-excel',
        ppt: 'fa-solid fa-file-powerpoint',
        pptx: 'fa-solid fa-file-powerpoint',
        txt: 'fa-solid fa-file-lines',
      };
      return docIcons[extension] || 'fa-regular fa-file-lines wa-file-type-doc';
    }
  };

  const handleCategorySelect = category => {
    setSelectedCategory(category);
    if (category === 'audio') {
      setCaption('');
    }
    setTimeout(() => fileInputRef.current?.click(), 150);
  };

  const handleFileSelect = e => {
    const files = Array.from(e.target.files);
    let newFiles = [];

    for (const file of files) {
      if (selectedFiles.length + newFiles.length >= MAX_FILES) {
        alert(`You can only upload up to ${MAX_FILES} files.`);
        break;
      }

      const category = selectedCategory || 'document';
      const limit = FILE_LIMITS[category];
      if (file.size > limit) {
        alert(
          `${file.name} exceeds the size limit for ${category} (${(limit / 1024 / 1024).toFixed(0)}MB)`
        );
        continue;
      }

      newFiles.push(file);
    }

    if (newFiles.length) {
      setSelectedFiles(prev => [...prev, ...newFiles]);
      // Auto-set category based on first file if not already set
      if (!selectedCategory && newFiles[0]) {
        const fileType = newFiles[0].type.split('/')[0];
        const cat = fileType === 'application' ? 'document' : fileType;
        setSelectedCategory(cat);
        if (cat === 'audio') {
          setCaption('');
        }
      }
    }
    e.target.value = '';
  };

  const handleAddMore = () => {
    if (selectedCategory) fileInputRef.current?.click();
  };

  const handleSafeClose = () => {
    if (!uploading) {
      onClose();
    }
  };

  const removeFile = index => {
    if (!uploading) {
      setSelectedFiles(prev => {
        const updated = prev.filter((_, i) => i !== index);
        if (updated.length === 0) {
          setSelectedCategory(null);
          setCaption('');
        }
        return updated;
      });
    }
  };

  const formatFileSize = bytes => {
    if (!bytes) return '';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(2))} ${sizes[i]}`;
  };

  const onSend = async () => {
    if (uploading) return;
    if (selectedFiles.length === 0) {
      return alert('Please select at least one file.');
    }
    if (selectedFiles.length > 10) {
      return alert('You can select a maximum of 10 files.');
    }

    setUploading(true);
    try {
      const watconnectApi = new WatconnectApi('webhook_template');
      const historyApi = new DataApi('watconnect');
      const attachmentsApi = new DataApi('attachments');

      // 🚀 Step 1: Loop through and send each file
      for (const [i, file] of selectedFiles.entries()) {
        const messageType = selectedCategory;
        const fileName = file.name;
        const isLastFile = i === selectedFiles.length - 1;

        // 1️⃣ Upload file to WhatsApp
        const formData = new FormData();
        formData.append('messaging_product', 'whatsapp');
        formData.append('file', file);
        formData.append('description', messageType);

        const uploadResponse = await watconnectApi.post(
          `/documentId?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting)}`,
          formData
        );

        const documentId = uploadResponse?.data?.id || uploadResponse?.id;
        if (!documentId) throw new Error('Failed to get document ID');

        // 2️⃣ Prepare WhatsApp payload
        const whatsappPayload = {
          messaging_product: 'whatsapp',
          recipient_type: 'individual',
          to: recipientPhone.startsWith('+')
            ? recipientPhone
            : `+${recipientPhone}`,
        };

        const mediaCaption = isLastFile ? caption || '' : '';

        if (messageType === 'image') {
          whatsappPayload.type = 'image';
          whatsappPayload.image = { id: documentId, caption: mediaCaption };
        } else if (messageType === 'video') {
          whatsappPayload.type = 'video';
          whatsappPayload.video = { id: documentId, caption: mediaCaption };
        } else if (messageType === 'audio') {
          whatsappPayload.type = 'audio';
          whatsappPayload.audio = { id: documentId };
        } else {
          whatsappPayload.type = 'document';
          whatsappPayload.document = {
            id: documentId,
            filename: fileName,
            caption: mediaCaption,
          };
        }

        // 3️⃣ Send message to WhatsApp
        const messageResponse = await watconnectApi.post(
          `/message?whatsapp_setting_number=${encodeURIComponent(selectedWhatsAppSetting)}`,
          whatsappPayload
        );

        const messageId = messageResponse?.messages?.[0]?.id || null;

        // 4️⃣ Create history for this file
        const historyPayload = {
          name: recipientName || '',
          whatsapp_number: recipientPhone.replace(/^\+/, ''),
          message: isLastFile ? caption || '' : '',
          status: 'Outgoing',
          business_number: selectedWhatsAppSetting,
          delivery_status: 'sent',
          message_id: messageId,
        };

        const historyRes = await historyApi.post(`/history`, historyPayload);
        const historyRecord = historyRes?.data?.data || historyRes;

        if (historyRecord?.id) {
          // 5️⃣ Upload this specific file as attachment
          const fileForm = new FormData();
          fileForm.append('files', file);
          fileForm.append('description', `${messageType} file for WatConnect`);

          const attachmentRes = await attachmentsApi.post(
            `/${historyRecord.id}`,
            fileForm
          );

          const attachments = attachmentRes?.data?.data || [];

          // 6️⃣ Add params for this file
          await Promise.all(
            attachments.map(async att => {
              const paramData = {
                id: historyRecord.id,
                parameters: {
                  file_url: att.file_url,
                  file_type: att.filetype,
                },
              };
              await historyApi.post(`/history`, paramData);
            })
          );
        }
      }

      onSendComplete?.();
      onClose();
    } catch (err) {
      console.error('❌ Error:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const renderFilePreview = (file, index) => {
    let previewContent;

    if (file.type.startsWith('image/')) {
      previewContent = (
        <img
          src={URL.createObjectURL(file)}
          alt={file.name}
          className='wa-file-preview-img'
        />
      );
    } else if (file.type.startsWith('video/')) {
      previewContent = (
        <div className='wa-file-preview-video'>
          <i className='fa-solid fa-video'></i>
        </div>
      );
    } else if (file.type.startsWith('audio/')) {
      previewContent = (
        <div className='wa-file-preview-audio'>
          <i className='fa-solid fa-music'></i>
        </div>
      );
    } else {
      previewContent = (
        <div className='wa-file-preview-doc'>
          <i className={getFileIcon(file)}></i>
        </div>
      );
    }

    return (
      <div className='wa-file-preview-card' key={index}>
        <button
          className='wa-remove-btn'
          onClick={() => !uploading && removeFile(index)}
          disabled={uploading}
          title={uploading ? 'Sending in progress...' : 'Remove file'}
          style={uploading ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
        >
          ✕
        </button>
        {previewContent}
        <div className='wa-file-info'>
          <span className='wa-file-name' title={file.name}>
            {file.name.length > 15
              ? `${file.name.substring(0, 12)}...`
              : file.name}
          </span>
          <small className='wa-file-size'>{formatFileSize(file.size)}</small>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className='wa-file-attachment-modal'>
      <div className='wa-file-modal-content'>
        {/* Category Selection */}

        <div className='wa-file-preview-header'>
          <div className='wa-file-preview-title'>Select Files</div>
          <button
            className='wa-add-more-btn'
            onClick={handleSafeClose}
            disabled={uploading}
            title={uploading ? 'Sending in progress...' : 'Close'}
            style={uploading ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
          >
            ✕
          </button>
        </div>
        {!selectedFiles.length && (
          <div className='wa-file-category-grid'>
            {Object.entries(fileCategories).map(([key, cat]) => (
              <div
                key={key}
                className={`wa-category-item ${key} ${uploading ? 'disabled' : ''}`}
                onClick={() => !uploading && handleCategorySelect(key)}
                style={uploading ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
              >
                <i className={cat.icon}></i>
                <span>{cat.label}</span>
                <div className='wa-category-info'>{cat.description}</div>
              </div>
            ))}
          </div>
        )}

        {/* File Preview Section */}
        {selectedFiles.length > 0 && (
          <div className='wa-file-preview-section'>
            {/* File Previews */}
            <div className='wa-file-preview-list'>
              {selectedFiles.map(renderFilePreview)}

              {selectedFiles.length < MAX_FILES && (
                <div
                  className={`wa-add-more-card ${uploading ? 'disabled' : ''}`}
                  onClick={() => !uploading && handleAddMore()}
                  style={uploading ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                >
                  <i className='fa-solid fa-plus'></i>
                  <span>Add More Files</span>
                  <small>{MAX_FILES - selectedFiles.length} remaining</small>
                </div>
              )}
            </div>

            {/* Caption Input */}
            <div className='wa-caption-box'>
              <input
                type='text'
                placeholder={
                  selectedCategory === 'audio'
                    ? 'Captions are not supported for audio files'
                    : 'Add a caption (optional)...'
                }
                value={selectedCategory === 'audio' ? '' : caption}
                onChange={e => setCaption(e.target.value)}
                maxLength={1024}
                disabled={uploading || selectedCategory === 'audio'}
                style={
                  selectedCategory === 'audio'
                    ? { backgroundColor: '#f0f2f5', cursor: 'not-allowed' }
                    : {}
                }
              />
            </div>

            {/* Footer */}
            <div className='wa-file-modal-footer'>
              <div className='wa-file-counter'>
                <span>
                  {selectedFiles.length} file
                  {selectedFiles.length !== 1 ? 's' : ''} selected
                </span>
              </div>
              <div className='wa-footer-actions'>
                <button
                  className='wa-cancel-btn'
                  onClick={handleSafeClose}
                  disabled={uploading}
                  style={uploading ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                >
                  Cancel
                </button>
                <button
                  className='wa-send-file-btn'
                  onClick={onSend}
                  disabled={uploading || selectedFiles.length === 0}
                >
                  {uploading ? (
                    <>
                      <i className='fa-solid fa-spinner fa-spin'></i> Sending...
                    </>
                  ) : (
                    <>
                      <i className='fa-solid fa-paper-plane pe-2'></i>Send{' '}
                      {selectedFiles.length} File
                      {selectedFiles.length > 1 ? 's' : ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hidden File Input */}
        <input
          type='file'
          ref={fileInputRef}
          accept={
            selectedCategory ? fileCategories[selectedCategory]?.accept : '*/*'
          }
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          multiple
        />
      </div>
    </div>
  );
};

export default FileAttachmentModal;
